import type {ColorMap} from './colors';
import type {CSS, CSSProperties} from './types';

/** How wide the focus ring is, in CSS pixels (WCAG 2.4.7 and 1.4.11). */
export const FOCUS_RING_WIDTH = 2;

/** How long the focus ring takes to fade in. */
export const FOCUS_RING_FADE = '0.2s';

/**
 * `default` for most controls. `danger` for a control that is already red: a
 * field that is not valid, or a destructive row of a menu. `accent` for a
 * primary button, whose ring matches its fill (black in light mode, blue in
 * dark mode). The accent ring clears 3:1 only on the page and on cards, which
 * is where primary buttons go; they never go in a `Callout`, which lint checks.
 * `onAccent` for a ring inside a primary button, where it meets the fill: it
 * is the color of the button's label (`accentBlueText`).
 */
export type FocusRingTone = 'default' | 'danger' | 'accent' | 'onAccent';

/** The color token of the ring in each tone. */
export const FOCUS_RING_TONES: Record<FocusRingTone, keyof ColorMap> = {
  default: 'focusRing',
  danger: 'borderRedLight',
  accent: 'accentBlue',
  onAccent: 'accentBlueText',
};

// Only the color changes, so this is not motion, and it runs under
// `prefers-reduced-motion` too (WCAG 2.3.3 is about movement). It has no `to`:
// it ends on the ring's own color, whatever the tone.
const FADE_IN = {from: {outlineColor: 'transparent'}};

/**
 * The focus indicator of every control in the design system: a 2px outline in
 * the `focusRing` token (or, for `tone: 'danger'`, `borderRedLight`), which
 * clears 3:1 against every surface the controls sit on, in light and dark mode
 * (`colors.contrast.test.ts`). It fades in over 0.2s.
 *
 * `offset` says where it goes:
 *
 * - `2`, the default, puts it outside the control with a gap between them. The
 *   gap shows the surface, so the ring is next to the surface only, whatever
 *   the control's own fill. Use it unless the ring would be cut off.
 * - `-2` puts it inside the control's edge, for a control whose neighbours
 *   touch it (a row in a menu, a cell) or whose parent hides overflow. The
 *   ring is then next to the control's fill, which must be a pair in the
 *   contrast test.
 *
 * It is an outline and not a box shadow: forced colors mode removes box
 * shadows, and it keeps outlines in a system color.
 */
export const focusRing = (
  colors: ColorMap,
  offset: number = 2,
  tone: FocusRingTone = 'default',
): CSSProperties => ({
  animationDuration: FOCUS_RING_FADE,
  animationName: FADE_IN,
  animationTimingFunction: 'ease-out',
  outline: `${FOCUS_RING_WIDTH}px solid ${colors[FOCUS_RING_TONES[tone]]}`,
  outlineOffset: `${offset}px`,
});

/**
 * `focusRing` on `:focus-visible`: when the keyboard moves focus, and on every
 * focus of a text field. Browsers draw no outline of their own on a `:focus`
 * that is not `:focus-visible`, so nothing has to remove one.
 *
 * Do not also set `outline` on `:focus` for the same element. The two
 * selectors are equally specific, and Styletron inserts each rule the first
 * time any component uses it, so which one wins depends on what rendered
 * first.
 */
export const focusVisibleRing = (
  colors: ColorMap,
  offset?: number,
  tone?: FocusRingTone,
): CSS => ({
  ':focus-visible': focusRing(colors, offset, tone),
});
